import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { Session } from 'next-auth';

import {
  fetchEmployeeDetailsByEmployeeId,
} from '~/entities/Employee/api/employee.api';
import { GET_EMPLOYEE_DETAILS_QUERY_KEY } from '~/entities/Employee/api/useEmployeeDetails';
import type { GetEmployeeRoleAPIResponse } from '~/entities/RoleSetting/api/useGetEmployeeRole';
import { User } from '~/shared/auth/useUser';
import { roleFlags } from '~/shared/utils/roleFlags';

const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA_ID ?? '';

type AnalyticEvents =
  | 'click_coi'
  | 'click_gd'
  | 'click_ocm'
  | 'click_employee_movements'
  | 'click_cancel'
  | 'click_global_policies'
  | 'click_global_trainings'
  | 'click_help_line'
  | 'click_access_helpline'
  | 'click_filter_category'
  | 'click_filter_zone'
  | 'click_filter_status'
  | 'click_filter_bu'
  | 'click_filter_created_date'
  | 'click_filter_end_date'
  | 'click_cards_view'
  | 'click_edit'
  | 'click_assign'
  | 'click_chat'
  | 'click_approval'
  | 'click_ticket_activity';

type GTagEvent = {
  action: AnalyticEvents;
  category: string;
  label: string;
  filters?: unknown;
  value: number;
  user: User;
  employeeRole?: GetEmployeeRoleAPIResponse | null;
  employeeDetails?: { workCountry?: string; countryBU?: string } | null;
};

function fireEvent({
  action,
  category,
  label,
  value,
  user,
  filters,
  employeeRole,
  employeeDetails,
}: GTagEvent) {
  if (!GA_TRACKING_ID) {
    return;
  }
  if (!window.gtag) return;
  const roles = roleFlags(employeeRole?.role);
  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value,
    filters,
    user_properties: {
      globalId: user.globalId,
      band: user.band,
      zone: user.zone,
      isManager: roles.isManager,
      isSuperAdmin: roles.isSuperAdmin,
      isGlobalAdmin: roles.isGlobalAdmin,
      isZoneAdmin: roles.isZoneAdmin,
      isPBP: roles.isPBP,
      isTalentManager: roles.isTalentManager,
      country: employeeDetails?.workCountry ?? employeeDetails?.countryBU,
    },
  });
}

function usePageView(
  session?: Session | null,
  employeeDetails?: { workCountry?: string; countryBU?: string } | null
) {
  const router = useRouter();
  const sessionUser = session?.user as { employeeId?: string; zone?: string } | undefined;
  const employeeId = sessionUser?.employeeId;
  const zone = sessionUser?.zone;
  const country = employeeDetails?.workCountry ?? employeeDetails?.countryBU;

  useEffect(() => {
    function handleRouteChange(url: URL) {
      if (!GA_TRACKING_ID && !session?.user) return;
      if (!window.gtag) return;
      window.gtag('config', GA_TRACKING_ID, {
        page_path: url,
        user_properties: {
          country,
          employeeId,
          zone,
        },
      });
    }

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, session?.user, country, employeeId, zone]);
}

type GoogleAnalyticsScriptProps = {
  session?: Session | null;
};

function GoogleAnalyticsScript({ session }: GoogleAnalyticsScriptProps) {
  const STALE_MS_PER_SEC = 1000;
  const STALE_SEC_PER_MIN = 60;
  const STALE_MINUTES = 5;
  const user = session?.user as { employeeId?: string; proxiedAs?: { employeeId?: string } };
  const employeeId = user?.proxiedAs?.employeeId ?? user?.employeeId;
  const { data: employeeDetails } = useQuery({
    queryKey: [GET_EMPLOYEE_DETAILS_QUERY_KEY, employeeId],
    queryFn: () => fetchEmployeeDetailsByEmployeeId(employeeId!),
    enabled: !!employeeId,
    staleTime: STALE_MS_PER_SEC * STALE_SEC_PER_MIN * STALE_MINUTES,
  });
  usePageView(session, employeeDetails);

  if (!GA_TRACKING_ID) {
    return null;
  }

  return (
    <>
      <Script
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`}
      />
      <Script id="gtag-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_TRACKING_ID}', {
            page_path: window.location.pathname,
          });
        `}
      </Script>
    </>
  );
}

export const GA = {
  fireEvent,
  Script: GoogleAnalyticsScript,
};
